import React, { useState, useMemo } from 'react';
import Table, { TableColumn } from '../../components/Table/Table';
import { PettyCashForm } from '../../components/Form';
import { PettyCash } from '../../types';
import { usePettyCash, useCreatePettyCash, useUpdatePettyCash, useDeletePettyCash } from '../../hooks/usePettyCash';
import { useBranches } from '../../hooks/useBranches';
import { useToast } from '../../contexts/ToastContext';
import PettyCashLetterGenerator from '../../components/HR/PettyCashLetterGenerator';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { PageHeader, FilterBar, StatsCards, ErrorState } from '../../components/Common';
import type { FilterConfig, StatCard } from '../../components/Common';

// Stored totals can be stale on old records; always derive from the expense rows
const calcExpenses = (row: PettyCash) =>
  (row.expenses || []).reduce((s, e) => s + (Number(e.amount) || 0), 0);
const calcFinal = (row: PettyCash) =>
  (Number(row.previousBalance) || 0) + (Number(row.totalReceived) || 0) - calcExpenses(row);

const PettyCashPage: React.FC = () => {
  const { showToast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState<PettyCash | undefined>();
  const [showGenerator, setShowGenerator] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<PettyCash | undefined>();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');
  const [balanceFromFilter, setBalanceFromFilter] = useState<string>('');
  const [balanceToFilter, setBalanceToFilter] = useState<string>('');
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const queryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 100 }
  }), []);

  const { data: records = [], isLoading, error, refetch } = usePettyCash(queryOptions);
  const branchesQO = useMemo(() => ({
    sort: [{ field: 'name', order: 'asc' as const }],
    pagination: { pageSize: 200 },
  }), []);
  const { data: branches = [] } = useBranches(branchesQO);

  const createMutation = useCreatePettyCash(() => {
    showToast('Petty cash record created successfully', 'success');
    refetch(); setShowForm(false); setEditingRecord(undefined);
  });

  const updateMutation = useUpdatePettyCash(() => {
    showToast('Petty cash record updated successfully', 'success');
    refetch(); setShowForm(false); setEditingRecord(undefined);
  });

  const deleteMutation = useDeletePettyCash(() => {
    showToast('Petty cash record deleted successfully', 'success');
    refetch();
  });

  const filteredRecords = useMemo(() => {
    return (records || []).filter(record => {
      const matchesSearch = !searchTerm || (
        record.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.employeeId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.expenses.some(expense => 
          expense.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          expense.class.toLowerCase().includes(searchTerm.toLowerCase()) ||
          expense.category.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
      const recordDate = new Date(record.date);
      const matchesDateFrom = !dateFromFilter || recordDate >= new Date(dateFromFilter);
      const matchesDateTo = !dateToFilter || recordDate <= new Date(dateToFilter);
      const matchesBalanceFrom = !balanceFromFilter || calcFinal(record) >= parseFloat(balanceFromFilter);
      const matchesBalanceTo = !balanceToFilter || calcFinal(record) <= parseFloat(balanceToFilter);
      const matchesLocation = locationFilter === 'all' || record.location.toLowerCase().includes(locationFilter.toLowerCase());
      const matchesStatus = statusFilter === 'all' || record.status === statusFilter;
      return matchesSearch && matchesDateFrom && matchesDateTo && 
             matchesBalanceFrom && matchesBalanceTo && matchesLocation && matchesStatus;
    });
  }, [records, searchTerm, dateFromFilter, dateToFilter, balanceFromFilter, balanceToFilter, locationFilter, statusFilter]);

  const columns: TableColumn[] = [
    { key: 'employeeName', label: 'Employee Name - اسم الموظف',
      render: (value: string, row: PettyCash) => (
        <div><div className="font-medium">{value}</div><div className="text-sm text-gray-500">ID: {row.employeeId}</div></div>
      ) },
    { key: 'date', label: 'Date - التاريخ', sortable: true, render: (value: string) => new Date(value).toLocaleDateString() },
    { key: 'location', label: 'Location - الموقع', sortable: true },
    { key: 'previousBalance', label: 'Previous Balance - الرصيد السابق', sortable: true,
      render: (value: number) => <span className="font-mono text-green-600">{value.toFixed(2)} EGP</span> },
    { key: 'totalReceived', label: 'Total Received - إجمالي المستلم', sortable: true,
      render: (value: number) => <span className="font-mono text-blue-600">{value.toFixed(2)} EGP</span> },
    { key: 'totalExpenses', label: 'Total Expenses - إجمالي المصروف', sortable: true,
      render: (_: number, row: PettyCash) => <span className="font-mono text-red-600">{calcExpenses(row).toFixed(2)} EGP</span> },
    { key: 'finalBalance', label: 'Final Balance - الرصيد النهائي', sortable: true,
      render: (_: number, row: PettyCash) => (
        <span className={`font-mono font-semibold ${calcFinal(row) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{calcFinal(row).toFixed(2)} EGP</span>
      ) },
    { key: 'expenses', label: 'Expenses Count - عدد المصروفات',
      render: (value: any[], row: PettyCash) => <span className="px-2 py-1 bg-gray-100 rounded-full text-sm">{value?.length || 0} items</span> },
    { key: 'status', label: 'Status - الحالة', sortable: true,
      render: (value: string) => {
        const cfg = { pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
          approved: { label: 'Approved', color: 'bg-green-100 text-green-800' },
          rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800' }
        }[value as string] || { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' };
        return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>{cfg.label}</span>;
      } },
    { key: 'actions', label: 'Actions',
      render: (value: any, row: PettyCash) => (
        <div className="flex flex-nowrap items-center gap-1 whitespace-nowrap">
          {row.status === 'pending' && (<>
            <button onClick={() => handleApprove(row)} className="px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-xs font-medium">Approve</button>
            <button onClick={() => handleReject(row)} className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-xs font-medium">Reject</button>
          </>)}
          <button onClick={() => handleEdit(row)} className="px-2 py-1 bg-yellow-600 text-white rounded hover:bg-yellow-700 text-xs font-medium">Edit</button>
          <button onClick={() => handleDelete(row)} className="px-2 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 text-xs font-medium">Delete</button>
          <button onClick={() => handleGenerateReport(row)} className="px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs font-medium">Report</button>
        </div>
      ) }
  ];

  const handleEdit = (record: PettyCash) => { setEditingRecord(record); setShowForm(true); };
  const handleDelete = async (record: PettyCash) => {
    if (window.confirm('Are you sure you want to delete this petty cash record?')) {
      try { await deleteMutation.execute(record); } catch (error) { console.error('Delete petty cash error:', error); }
    }
  };
  const handleAdd = () => { setEditingRecord(undefined); setShowForm(true); };

  const handleFormSubmit = async (data: Partial<PettyCash>) => {
    if (editingRecord) {
      try {
        const updatedData = { ...editingRecord, ...data };
        await updateMutation.execute({ id: editingRecord.id, data: updatedData });
      } catch (error) { console.error('Update petty cash error:', error); }
    } else {
      try { await createMutation.execute(data); } catch (error) { console.error('Create petty cash error:', error); }
    }
  };

  const handleFormCancel = () => { setShowForm(false); setEditingRecord(undefined); };
  const handleGenerateReport = (record: PettyCash) => { setSelectedRecord(record); setShowGenerator(true); };

  const handleApprove = async (record: PettyCash) => {
    if (window.confirm('Are you sure you want to approve this petty cash record?')) {
      try {
        const updatedData = { ...record, status: 'approved' as const, approvedBy: 'Current User', approvedAt: new Date().toISOString() };
        await updateMutation.execute({ id: record.id, data: updatedData });
        showToast('Petty cash record approved successfully', 'success');
      } catch (error) { console.error('Approve petty cash error:', error); }
    }
  };

  const handleReject = async (record: PettyCash) => {
    const reason = window.prompt('Please enter the reason for rejection:');
    if (reason) {
      try {
        const updatedData = { ...record, status: 'rejected' as const, rejectedBy: 'Current User', rejectedAt: new Date().toISOString(), rejectedReason: reason };
        await updateMutation.execute({ id: record.id, data: updatedData });
        showToast('Petty cash record rejected', 'info');
      } catch (error) { console.error('Reject petty cash error:', error); }
    }
  };

  const handleCloseGenerator = () => { setShowGenerator(false); setSelectedRecord(undefined); };

  const buildExportData = () => filteredRecords.map(record => ({
    'Date': record.date, 'Location': record.location,
    'Previous Balance (EGP)': record.previousBalance, 'Total Received (EGP)': record.totalReceived,
    'Total Expenses (EGP)': calcExpenses(record), 'Final Balance (EGP)': calcFinal(record),
    'Expenses Count': record.expenses?.length || 0,
    'Status': record.status.charAt(0).toUpperCase() + record.status.slice(1),
    'Approved By': record.approvedBy || 'N/A', 'Rejected By': record.rejectedBy || 'N/A',
    'Rejection Reason': record.rejectedReason || 'N/A'
  }));
  const handleExport = () => exportToExcel(buildExportData(), `petty_cash_${new Date().toISOString().split('T')[0]}`);
  const handlePrint = () => printAsExcel(buildExportData(), 'Petty Cash');

  const clearFilters = () => {
    setSearchTerm(''); setStatusFilter('all'); setLocationFilter('all');
    setDateFromFilter(''); setDateToFilter(''); setBalanceFromFilter(''); setBalanceToFilter('');
  };

  const statsCards: StatCard[] = [
    { label: 'Pending', value: records.filter(r => r.status === 'pending').length, color: 'yellow', icon: <span>⏳</span> },
    { label: 'Approved', value: records.filter(r => r.status === 'approved').length, color: 'green', icon: <span>✓</span> },
    { label: 'Rejected', value: records.filter(r => r.status === 'rejected').length, color: 'red', icon: <span>✕</span> },
  ];

  const filters: FilterConfig[] = [
    { key: 'status', label: 'Status', type: 'select', value: statusFilter, onChange: setStatusFilter,
      options: [{ value: 'all', label: 'All Status' }, { value: 'pending', label: 'Pending' }, { value: 'approved', label: 'Approved' }, { value: 'rejected', label: 'Rejected' }] },
    { key: 'location', label: 'Location', type: 'select', value: locationFilter, onChange: setLocationFilter,
      options: [
        { value: 'all', label: 'All Locations' },
        ...(branches as any[])
          .filter((b: any) => b.name)
          .map((b: any) => ({
            value: b.name,
            label: `${b.name}${b.type === 'vessel' ? ' (Vessel)' : b.type === 'office' ? ' (HQ)' : ''}`,
          })),
      ] },
    { key: 'dateFrom', label: 'Date From', type: 'date', value: dateFromFilter, onChange: setDateFromFilter },
    { key: 'dateTo', label: 'Date To', type: 'date', value: dateToFilter, onChange: setDateToFilter },
    { key: 'balanceFrom', label: 'Min Final Balance', type: 'number', value: balanceFromFilter, onChange: setBalanceFromFilter, placeholder: '0.00', step: '0.01' },
    { key: 'balanceTo', label: 'Max Final Balance', type: 'number', value: balanceToFilter, onChange: setBalanceToFilter, placeholder: '999999.99', step: '0.01' },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ocean-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading petty cash records...</p>
        </div>
      </div>
    );
  }

  if (error) return <ErrorState entityName="petty cash records" onRetry={() => refetch()} />;

  if (showForm) {
    return (
      <div className="p-6">
        <PettyCashForm pettyCash={editingRecord} onSubmit={handleFormSubmit} onCancel={handleFormCancel} onApprove={handleApprove} onReject={handleReject} />
      </div>
    );
  }

  if (showGenerator && selectedRecord) {
    return (
      <div className="p-6">
        <PettyCashLetterGenerator pettyCash={selectedRecord} onClose={handleCloseGenerator} onGenerate={handlePrint} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader title="Petty Cash Management" description="إدارة العهدة المالية - Manage daily cash transactions for employees" />
      <StatsCards stats={statsCards} columns={3} />

      <FilterBar
        searchTerm={searchTerm} onSearchChange={setSearchTerm} searchPlaceholder="Search petty cash records..."
        filters={filters} onClearFilters={clearFilters}
        totalCount={records.length} filteredCount={filteredRecords.length}
        entityName="Petty Cash Records - سجلات العهدة المالية"
        onAdd={handleAdd} addLabel="Add Petty Cash Record" onExport={handleExport} onPrint={handlePrint}
      />

      <div className="card">
        <Table columns={columns} data={filteredRecords} title="" searchPlaceholder="" />
      </div>
    </div>
  );
};

export default PettyCashPage;
